import { useCallback, useEffect, useRef, useState } from "react";

const isAbortError = (error) => error?.name === "AbortError";

/**
 * Loads a support snapshot and can refresh it without blanking the last good data.
 * Polling pauses while the tab is hidden and resumes immediately when it becomes visible.
 */
const useSupportResource = ({
  load,
  resourceKey,
  pollIntervalMs = 0,
  requestTimeoutMs = 10_000,
}) => {
  const loadRef = useRef(load);
  const requestTimeoutRef = useRef(requestTimeoutMs);
  const activeRequest = useRef(null);
  const requestSequence = useRef(0);
  const lastAttemptAt = useRef(0);
  const [state, setState] = useState({
    data: null,
    loading: true,
    refreshing: false,
    error: null,
    lastSuccessfulAt: null,
  });

  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    requestTimeoutRef.current = requestTimeoutMs;
  }, [requestTimeoutMs]);

  const refresh = useCallback(async ({ background = false } = {}) => {
    if (activeRequest.current) return null;

    const controller = new AbortController();
    const requestId = ++requestSequence.current;
    const request = { controller, timeoutId: null, timedOut: false };
    activeRequest.current = request;
    lastAttemptAt.current = Date.now();
    setState((previous) => ({
      ...previous,
      loading: previous.data ? false : !background,
      refreshing: Boolean(previous.data),
      error: background ? previous.error : null,
    }));

    try {
      const timeoutMs = Number(requestTimeoutRef.current);
      const loadPromise = Promise.resolve().then(() =>
        loadRef.current({ signal: controller.signal })
      );
      const data = Number.isFinite(timeoutMs) && timeoutMs > 0
        ? await Promise.race([
            loadPromise,
            new Promise((_, reject) => {
              request.timeoutId = window.setTimeout(() => {
                request.timedOut = true;
                controller.abort();
                reject(
                  new Error(
                    `Support data request timed out after ${Math.ceil(
                      timeoutMs / 1000
                    )} seconds.`
                  )
                );
              }, timeoutMs);
            }),
          ])
        : await loadPromise;
      if (requestId !== requestSequence.current) return null;
      const successfulAt = new Date();
      setState({
        data,
        loading: false,
        refreshing: false,
        error: null,
        lastSuccessfulAt: successfulAt,
      });
      return data;
    } catch (error) {
      if (
        (isAbortError(error) && !request.timedOut) ||
        requestId !== requestSequence.current
      ) {
        return null;
      }
      setState((previous) => ({
        ...previous,
        loading: false,
        refreshing: false,
        error: request.timedOut
          ? `Support data request timed out after ${Math.ceil(
              Number(requestTimeoutRef.current) / 1000
            )} seconds.`
          : error?.message || "Support data is unavailable.",
      }));
      return null;
    } finally {
      if (request.timeoutId !== null) {
        window.clearTimeout(request.timeoutId);
      }
      if (activeRequest.current === request) activeRequest.current = null;
    }
  }, []);

  useEffect(() => {
    requestSequence.current += 1;
    const previousRequest = activeRequest.current;
    previousRequest?.controller.abort();
    if (previousRequest && previousRequest.timeoutId !== null) {
      window.clearTimeout(previousRequest.timeoutId);
    }
    activeRequest.current = null;
    setState({
      data: null,
      loading: true,
      refreshing: false,
      error: null,
      lastSuccessfulAt: null,
    });
    void refresh();

    return () => {
      requestSequence.current += 1;
      const pendingRequest = activeRequest.current;
      pendingRequest?.controller.abort();
      if (pendingRequest && pendingRequest.timeoutId !== null) {
        window.clearTimeout(pendingRequest.timeoutId);
      }
      activeRequest.current = null;
    };
  }, [refresh, resourceKey]);

  useEffect(() => {
    if (!pollIntervalMs) return undefined;

    const poll = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        void refresh({ background: true });
      }
    };
    const timer = window.setInterval(poll, pollIntervalMs);
    const onVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastAttemptAt.current >= pollIntervalMs
      ) {
        poll();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [pollIntervalMs, refresh]);

  return { ...state, refresh };
};

export default useSupportResource;
